// ABOUTME: Gamification constants for XP, levels, and rewards
// ABOUTME: Centralized configuration for game mechanics

/**
 * Experience points required per level
 * Level calculation: level = floor(totalXP / EXPERIENCE_PER_LEVEL) + 1
 */
export const EXPERIENCE_PER_LEVEL = 1000

/**
 * Maximum number of recent activities to store
 */
export const MAX_ACTIVITY_ITEMS = 50

/**
 * XP Rewards for various actions
 */
export const XP_REWARDS = {
  /** Scanning a wardrobe item */
  SCAN_ITEM: 50,
  /** Completing an easy challenge */
  CHALLENGE_EASY: 100,
  /** Completing a medium challenge */
  CHALLENGE_MEDIUM: 250,
  /** Completing a hard challenge */
  CHALLENGE_HARD: 500,
  /** Daily login streak */
  DAILY_LOGIN: 25,
  /** Logging an outfit for the day */
  OUTFIT_LOGGED: 25,
  /** Accepting a daily suggestion */
  ACCEPT_SUGGESTION: 25,
  /** Completing a quiz */
  QUIZ_COMPLETION: 75,
  /** Finishing an education module */
  MODULE_COMPLETION: 150,
  /** Unlocking an achievement */
  ACHIEVEMENT: 200,
} as const

/**
 * Level titles and thresholds
 */
export const LEVEL_TITLES = [
  { level: 1, title: 'Initiate Gentleman', minXP: 0 },
  { level: 2, title: 'Colour Enthusiast', minXP: 1000 },
  { level: 3, title: 'Accessory Novice', minXP: 2000 },
  { level: 4, title: 'Smart Casual Stylist', minXP: 3000 },
  { level: 5, title: 'Fashion Historian', minXP: 4000 },
  { level: 6, title: 'Formalwear Apprentice', minXP: 5000 },
  { level: 7, title: 'Accessory Connoisseur', minXP: 6000 },
  { level: 8, title: 'Colour Master', minXP: 7000 },
  { level: 9, title: 'Gentlemen Scholar', minXP: 8000 },
  { level: 10, title: 'Master Gentleman', minXP: 9000 },
] as const

/**
 * Achievement IDs and metadata
 */
export const ACHIEVEMENTS = {
  FIRST_SCAN: {
    id: 'first_scan',
    title: 'First Steps',
    description: 'Scan your first wardrobe item',
    xpReward: 100,
  },
  FIRST_CHALLENGE: {
    id: 'first_challenge',
    title: 'Challenge Accepted',
    description: 'Complete your first challenge',
    xpReward: 150,
  },
  STYLE_STREAK_7: {
    id: 'style_streak_7',
    title: 'Style Streak',
    description: 'Complete 7 challenges in a row',
    xpReward: 500,
  },
  COLOR_MASTER: {
    id: 'color_master',
    title: 'Color Master',
    description: 'Complete all color theory lessons',
    xpReward: 300,
  },
  WARDROBE_GURU: {
    id: 'wardrobe_guru',
    title: 'Wardrobe Guru',
    description: 'Upload 50+ clothing items',
    xpReward: 750,
  },
  QUIZ_MASTER: {
    id: 'quiz_master',
    title: 'Quiz Master',
    description: 'Complete 20 quizzes with 80%+ score',
    xpReward: 600,
  },
  FASHION_HISTORIAN: {
    id: 'fashion_historian',
    title: 'Fashion Historian',
    description: 'Complete all fashion history modules',
    xpReward: 1000,
  },
  MASTER_GENTLEMAN: {
    id: 'master_gentleman',
    title: 'Master Gentleman',
    description: 'Reach Level 10',
    xpReward: 2000,
  },
} as const

/**
 * Challenge difficulty levels
 */
export const CHALLENGE_DIFFICULTY = {
  EASY: 'easy',
  MEDIUM: 'medium',
  HARD: 'hard',
} as const

/**
 * Activity types for tracking user actions
 */
export const ACTIVITY_TYPES = {
  LEVEL_UP: 'level_up',
  CHALLENGE_COMPLETED: 'challenge_completed',
  ACHIEVEMENT_UNLOCKED: 'achievement_unlocked',
  XP_GAINED: 'xp_gained',
} as const

/**
 * Get level title for a given XP amount
 */
export function getLevelTitle(experiencePoints: number): string {
  const level = Math.floor(experiencePoints / EXPERIENCE_PER_LEVEL) + 1
  const levelData = LEVEL_TITLES.find((l) => l.level === level)
  return levelData?.title || 'Aspiring Gentleman'
}

/**
 * Get progress percentage within current level
 */
export function getLevelProgress(experiencePoints: number): number {
  const currentLevelXP = experiencePoints % EXPERIENCE_PER_LEVEL
  return Math.floor((currentLevelXP / EXPERIENCE_PER_LEVEL) * 100)
}

/**
 * Get XP needed for next level
 */
export function getXPToNextLevel(experiencePoints: number): number {
  const currentLevelXP = experiencePoints % EXPERIENCE_PER_LEVEL
  return EXPERIENCE_PER_LEVEL - currentLevelXP
}
